import React, { useState, useEffect } from 'react';
import { LMSService, CourseService } from '../services/api';
import Modal from './common/Modal';
import { useSettings } from '../context/SettingsContext';
import { getTerminology } from '../utils/terminology';

const LMS = () => {
  const { settings } = useSettings();
  const terminology = React.useMemo(() => {
    return getTerminology(settings.school_type);
  }, [settings.school_type]);

  const courseTerm = terminology.course;
  const coursesTerm = terminology.courses;
  const [assignments, setAssignments] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Stats
  const [stats, setStats] = useState({ assignments: 0, materials: 0, submissions: 0 });

  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    course_id: '',
    due_date: ''
  });

  const [materialForm, setMaterialForm] = useState({
    title: '',
    description: '',
    course_id: '',
    type: 'link',
    url_or_path: ''
  });

  useEffect(() => {
    loadLMSData();
    loadCourses();
  }, []);

  const loadLMSData = async () => {
    try {
      const [assignmentsRes, materialsRes, statsRes] = await Promise.all([
        LMSService.getAssignments(),
        LMSService.getMaterials(),
        LMSService.getStats()
      ]);
      setAssignments(assignmentsRes.data);
      setMaterials(materialsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error loading LMS data", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await CourseService.getAll();
      setCourses(response.data);
    } catch (error) {
      console.error("Error loading courses", error);
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await LMSService.createAssignment(assignmentForm);
      setShowAssignmentModal(false);
      loadLMSData();
      setAssignmentForm({ title: '', description: '', course_id: '', due_date: '' });
    } catch (error) {
      console.error("Error creating assignment", error);
    } finally {
      setSaving(false);
    }
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await LMSService.createMaterial(materialForm);
      setShowMaterialModal(false);
      loadLMSData();
      setMaterialForm({ title: '', description: '', course_id: '', type: 'link', url_or_path: '' });
    } catch (error) {
      console.error("Error creating material", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4">
        <div>
          <h2 className="fw-bold">Learning Management System</h2>
          <p className="text-muted">Assignments, resources, and study materials</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid mb-5">
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Active Assignments</p>
            <h3 className="mb-0">{stats.assignments}</h3>
          </div>
          <div className="stat-icon bg-purple-light text-primary">
            <i className="bi bi-journal-text"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Study Resources</p>
            <h3 className="mb-0">{stats.materials}</h3>
          </div>
          <div className="stat-icon bg-blue-light text-info">
            <i className="bi bi-folder2-open"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Recent Submissions</p>
            <h3 className="mb-0">{stats.submissions}</h3>
          </div>
          <div className="stat-icon bg-green-light text-success">
            <i className="bi bi-cloud-upload"></i>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-md-6">
          <div className="card-box h-100 shadow-sm border-0">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
              <h5 className="fw-bold m-0"><i className="bi bi-journal-album me-2 text-primary"></i>Assignments</h5>
              <button className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={() => setShowAssignmentModal(true)}>
                <i className="bi bi-plus-lg"></i> New Task
              </button>
            </div>
            <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {loading ? <p className="text-center py-4 text-muted">Loading...</p> : (
                assignments.length > 0 ? (
                  <ul className="list-group list-group-flush">
                    {assignments.map(a => (
                      <li key={a.id} className="list-group-item border-0 px-0 d-flex justify-content-between align-items-center mb-3 bg-transparent hover-bg-light rounded p-2 transition-all">
                        <div className="d-flex align-items-center gap-3">
                          <div className="bg-primary-subtle rounded-circle p-2 text-primary d-flex align-items-center justify-content-center" style={{ width: 45, height: 45 }}>
                            <i className="bi bi-file-text-fill fs-5"></i>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold text-dark">{a.title}</h6>
                            <small className="text-muted d-block">Due: {a.due_date} | <span className="text-primary">{a.course?.name || `No ${courseTerm}`}</span></small>
                          </div>
                        </div>
                        <button className="btn btn-sm btn-light text-primary rounded-pill shadow-sm">Details</button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox text-muted display-6 mb-3"></i>
                    <p className="text-muted">No active assignments.</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card-box h-100 shadow-sm border-0">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
              <h5 className="fw-bold m-0"><i className="bi bi-collection-play me-2 text-warning"></i>Study Materials</h5>
              <button className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={() => setShowMaterialModal(true)}>
                <i className="bi bi-upload"></i> Upload
              </button>
            </div>
            <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {loading ? <p className="text-center py-4 text-muted">Loading...</p> : (
                materials.length > 0 ? (
                  <ul className="list-group list-group-flush">
                    {materials.map(m => (
                      <li key={m.id} className="list-group-item border-0 px-0 d-flex justify-content-between align-items-center mb-3 bg-transparent hover-bg-light rounded p-2 transition-all">
                        <div className="d-flex align-items-center gap-3">
                          <div className={`rounded-circle p-2 d-flex align-items-center justify-content-center ${m.type === 'video' ? 'bg-danger-subtle text-danger' : 'bg-warning-subtle text-warning'}`} style={{ width: 45, height: 45 }}>
                            <i className={`bi ${m.type === 'video' ? 'bi-play-fill fs-4' : 'bi-file-earmark-pdf-fill fs-5'}`}></i>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold text-dark">{m.title}</h6>
                            <small className="text-muted d-block">{m.type.toUpperCase()} | <span className="text-primary">{m.course?.name || `No ${courseTerm}`}</span></small>
                          </div>
                        </div>
                        <a href={m.url_or_path} target="_blank" rel="noreferrer" className="btn btn-icon btn-light text-primary rounded-circle shadow-sm" style={{ width: 35, height: 35 }} title="Open">
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-folder2-open text-muted display-6 mb-3"></i>
                    <p className="text-muted">No materials uploaded yet.</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      <Modal show={showAssignmentModal} onClose={() => setShowAssignmentModal(false)} title="Create Assignment">
        <form onSubmit={handleAssignmentSubmit}>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Title</label>
            <input type="text" className="form-control" value={assignmentForm.title} onChange={(e) => setAssignmentForm({ ...assignmentForm, title: e.target.value })} required />
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">{courseTerm}</label>
            <select className="form-select" value={assignmentForm.course_id} onChange={(e) => setAssignmentForm({ ...assignmentForm, course_id: e.target.value })} required>
              <option value="">Select {courseTerm}</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Due Date</label>
            <input type="date" className="form-control" value={assignmentForm.due_date} onChange={(e) => setAssignmentForm({ ...assignmentForm, due_date: e.target.value })} required />
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Description</label>
            <textarea className="form-control" value={assignmentForm.description} onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })} rows="3" />
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowAssignmentModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Material Modal */}
      <Modal show={showMaterialModal} onClose={() => setShowMaterialModal(false)} title="Add Study Material">
        <form onSubmit={handleMaterialSubmit}>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Title</label>
            <input type="text" className="form-control" value={materialForm.title} onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })} required />
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">{courseTerm}</label>
            <select className="form-select" value={materialForm.course_id} onChange={(e) => setMaterialForm({ ...materialForm, course_id: e.target.value })} required>
              <option value="">Select {courseTerm}</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Type</label>
            <select className="form-select" value={materialForm.type} onChange={(e) => setMaterialForm({ ...materialForm, type: e.target.value })}>
              <option value="link">Link</option>
              <option value="pdf">PDF</option>
              <option value="video">Video</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">URL or Path</label>
            <input type="text" className="form-control" value={materialForm.url_or_path} onChange={(e) => setMaterialForm({ ...materialForm, url_or_path: e.target.value })} required />
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Description</label>
            <textarea className="form-control" value={materialForm.description} onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })} rows="3" />
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowMaterialModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Adding...' : 'Add Resource'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LMS;
